import { Card, Col, Divider, Radio, RadioChangeEvent, Row } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// components
import PageTitle from 'components/title/title';
import FilePreview from 'components/filePreview/filePreview';
import DisabledElement from 'components/disabledElement/disabledElement';
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';

// services

// models
import { AudienceType, FinancialStatementRequest, PeriodType } from '../models/financialStatement';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import NotificationService from 'core/services/notification';
import { onGetFinancialStatementById } from '../services/financialStatementServices';
import { isRTL } from 'core/services/language';
import { formatFileSize } from 'utils/FileSize';

// styles

interface ActionFinancialStatementViewPageProps {}

const ActionFinancialStatementViewPage: FunctionComponent<ActionFinancialStatementViewPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [financialStatement, setFinancialStatement] = useState<FinancialStatementRequest>();

	const audienceTypeOptions = [
		{
			value: 'Public',
			label: (
				<div>
					<p className="fw-semibold mb-0">{t('ACTIONS.PUBLIC')}</p>
					<p className="text-light mb-0">{t('ACTIONS.PUBLIC_DESC')}</p>
				</div>
			),
		},
		{
			value: 'Shareholders',
			label: (
				<div>
					<p className="fw-semibold mb-0">{t('COMMON.SHAREHOLDERS')}</p>
					<p className="text-light mb-0">{t('ACTIONS.TO_SENT_TO_SHAREHOLDERS')}</p>
				</div>
			),
		},
	];

	const periodTypeOptions = [
		{
			value: 'Annual',
			label: <p className="fw-semibold mb-0">{t('ACTIONS.ANNUAL')}</p>,
		},
		{
			value: 'HalfYear',
			label: <p className="fw-semibold mb-0">{t('ACTIONS.SEMI_ANNUAL')}</p>,
		},
		{
			value: 'Quarter',
			label: <p className="fw-semibold mb-0">{t('ACTIONS.QUARTER')}</p>,
		},
	];

	useEffect(() => {
		getFinancialStatementDetails();
	}, []);

	const getFinancialStatementDetails = async () => {
		try {
			setIsLoading(true);

			const response = await onGetFinancialStatementById(id as string);

			setFinancialStatement(response);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/financial-statement');
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			<Card>
				<ComponentHeader title={t('ACTIONS.FINANCIAL_STATEMENT_DETAILS')} />

				{isLoading ? (
					<LoadingComponent />
				) : (
					<>
						<div>
							<h6 className="mt-4 mb-2">{t('ACTIONS.AUDIENCE')}</h6>
							<Radio.Group
								disabled
								defaultValue={financialStatement?.audience}
								className="customRadio"
								style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
								options={audienceTypeOptions}
							/>
						</div>

						<div>
							<h6 className="mt-4 mb-2">{t('ACTIONS.PERIOD')}</h6>
							<Radio.Group
								disabled
								defaultValue={financialStatement?.period_type}
								style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
								options={periodTypeOptions}
							/>
						</div>

						<Row gutter={[16, 16]} className="mt-5">
							<Col span={24} md={12} lg={8}>
								<DisabledElement label={t('ACTIONS.YEAR')} value={financialStatement?.year} />
							</Col>
							{financialStatement?.period_type === 'Quarter' && (
								<Col span={24} md={12} lg={8}>
									<DisabledElement
										label={t('ACTIONS.QUARTER')}
										value={isRTL() ? financialStatement?.translated_period.ar : financialStatement?.translated_period.en}
									/>
								</Col>
							)}
							<Col span={24} md={12} lg={8}>
								<DisabledElement label={t('COMMON.TYPE')} value={t(`ACTIONS.${financialStatement?.type.toUpperCase()}`)} />
							</Col>
							<Col span={24} md={12}></Col>
						</Row>

						<Divider />

						<div>
							{financialStatement?.documents.map((document, index) => (
								<FilePreview
									key={index}
									name={document.title}
									data={formatFileSize(document.size_in_kilobyte)}
									downloadLink={document.download_link}
								/>
							))}
						</div>
					</>
				)}
			</Card>
		</>
	);
};

export default ActionFinancialStatementViewPage;
